// W17 filters. They live in the URL (typed search params) so a filtered list
// can be shared or bookmarked ("entregas con daño nuevo de esta semana").
import { normalizarPatente, tipoInspeccion } from '@taller/shared';
import { z } from 'zod';
import { fechaInspeccion, type InspeccionDoc } from '@/datos/tipos';

export const rangos = ['hoy', '7', '30', '90', 'todo'] as const;
export type Rango = (typeof rangos)[number];

export const busquedaInspecciones = z.object({
  tipo: tipoInspeccion.optional().catch(undefined),
  estado: z.enum(['borrador', 'firmada', 'anulada']).optional().catch(undefined),
  revision: z.enum(['pendiente', 'aprobada', 'observada']).optional().catch(undefined),
  rango: z.enum(rangos).optional().catch(undefined),
  patente: z.string().max(12).optional().catch(undefined),
  nuevo: z.boolean().optional().catch(undefined),
});

export type FiltrosInspecciones = z.infer<typeof busquedaInspecciones>;

export const RANGO_POR_DEFECTO: Rango = '30';

/** Lower bound (millis) for the Firestore query; null = no bound. */
export function desdeRango(rango: Rango | undefined, inicioHoy: number): number | null {
  const r = rango ?? RANGO_POR_DEFECTO;
  if (r === 'todo') return null;
  if (r === 'hoy') return inicioHoy;
  return inicioHoy - (Number(r) - 1) * 86_400_000;
}

export function danos(i: InspeccionDoc): number {
  return Object.values(i.zonas ?? {}).filter(
    (z) => z.estado === 'ray' || z.estado === 'gol' || z.estado === 'que',
  ).length;
}

/** Types compared against an Ingreso: their damage column counts new damage. */
export const esPosterior = (i: Pick<InspeccionDoc, 'tipo'>) =>
  i.tipo === 'reingreso' || i.tipo === 'entrega';

export function danosNuevos(i: InspeccionDoc): number {
  return (i.discrepancias ?? []).filter((d) => d.tipo === 'dano_nuevo' || d.tipo === 'empeora')
    .length;
}

export type EstadoVisible =
  'borrador' | 'firmada' | 'aprobada' | 'observada' | 'anulada' | 'invalida';

export function estadoVisible(i: InspeccionDoc): EstadoVisible {
  if (i.estado === 'anulada') return 'anulada';
  if (i.estado === 'borrador') return 'borrador';
  if (i.validacion && !i.validacion.ok) return 'invalida';
  if (i.revision?.resultado === 'observada') return 'observada';
  if (i.revision?.resultado === 'aprobada') return 'aprobada';
  return 'firmada';
}

/** Filters not expressible in the Firestore query (applied client-side). */
export function filtrar(lista: InspeccionDoc[], f: FiltrosInspecciones): InspeccionDoc[] {
  const pat = f.patente ? normalizarPatente(f.patente) : '';
  return lista.filter((i) => {
    if (f.tipo && i.tipo !== f.tipo) return false;
    if (f.estado && i.estado !== f.estado) return false;
    if (f.revision) {
      if (i.estado !== 'firmada') return false;
      const r = i.revision?.resultado ?? 'pendiente';
      if (r !== f.revision) return false;
    }
    if (pat && !i.patente.includes(pat)) return false;
    if (f.nuevo && danosNuevos(i) === 0) return false;
    return true;
  });
}

export function ordenarPorFecha(lista: InspeccionDoc[]): InspeccionDoc[] {
  return [...lista].sort(
    (a, b) => (fechaInspeccion(b)?.getTime() ?? 0) - (fechaInspeccion(a)?.getTime() ?? 0),
  );
}

/** UI names by type: `recepcion` is shown as "Ingreso" (shared/src/inspeccion.ts). */
export { nombreTipo } from '@taller/shared';
